import { useState, useMemo } from 'react';
import { useMetaData, useApi } from '../hooks/useApi';
import { useCategory } from '../context/CategoryContext';
import { useMonth } from '../context/MonthContext';
import { CategoryBehaviour } from '../utils/api';
import { BarChart, ChartCard, ChartLoader, ChartError } from '../components/Charts';
import { COLORS, fmtMonth } from '../utils/theme';

const BAR_COLOR       = COLORS.blue;   // all categories
const HIGHLIGHT_COLOR = COLORS.teal;   // the currently-selected category

const pctFmt = v => (v < 1 && v !== 0 ? `${(+v).toFixed(2)}%` : `${(+v).toFixed(1)}%`);

export default function CategoryComparisonNationalView() {
  const { category: globalCategory } = useCategory();
  const { sessionTypes, latestMonth, loading: metaLoading } = useMetaData();
  const { month } = useMonth();

  const [sessionType, setSessionType] = useState('open_app');
  const selectedMonth = month || latestMonth;

  const { data, loading, error } = useApi(
    () => CategoryBehaviour.penetrationCompare({ month_id: selectedMonth, session_type: sessionType }),
    { selectedMonth, sessionType },
    !!selectedMonth,
  );

  const { categories, series } = useMemo(() => {
    if (!data?.length) return { categories: [], series: [] };
    const categories = data.map(r => r.category);
    const values = data.map(r => {
      const v = +(r.penetration_pct ?? 0);
      return r.category === globalCategory
        ? { value: v, itemStyle: { color: HIGHLIGHT_COLOR, borderRadius: [0, 3, 3, 0] } }
        : v;
    });
    return { categories, series: [{ name: 'Penetration %', data: values, color: BAR_COLOR }] };
  }, [data, globalCategory]);

  if (metaLoading) return <div className="loading-state"><div className="spinner" /> Loading…</div>;

  return (
    <div>
      <div className="filters-bar">
        <span className="filter-label">Session</span>
        <select className="filter-select" value={sessionType} onChange={e => setSessionType(e.target.value)}>
          {sessionTypes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <ChartCard
        title="Category Penetration — National"
        sub={`${fmtMonth(selectedMonth)} · ${sessionType} · % of UK panel${globalCategory ? ' · selected category in teal' : ''}`}
      >
        {loading  && <ChartLoader height={420} />}
        {error    && <ChartError message={error} height={420} />}
        {!loading && !error && categories.length > 0 && (
          <BarChart
            categories={categories}
            series={series}
            height={Math.max(360, categories.length * 22 + 60)}
            horizontal
            xFormatter={pctFmt}
            tooltipFormatter={pctFmt}
          />
        )}
        {!loading && !error && categories.length === 0 && (
          <div className="empty-state">No penetration data for {fmtMonth(selectedMonth)}.</div>
        )}
      </ChartCard>
    </div>
  );
}
